/**
 * Main API sequence panel — replaces the canvas when in API view mode.
 *
 * @module FlowDesigner/api/ApiSequencePanel
 */

import React from 'react';
import { AnimatePresence } from 'framer-motion';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@chatsdet/ui';
import type { ApiSequenceConfig } from '@chatsdet/types';
import {
  useApiSequence,
  useApiSequenceSave,
  useApiSequenceDelete,
  useApiSequenceUpdateConfig,
  useApiOnlyToggle,
} from './hooks';
import { ApiSequenceEmptyState } from './ApiSequenceEmptyState';
import { ApiSequenceView } from './ApiSequenceView';
import { useSeedWorkflowOutputs } from '../../../../hooks/useSeedWorkflowOutputs';
import { useApiRequestList } from '../../../../features/api-catalog/api';
import { ApiSequenceEditor } from '../../../components/node-config/ApiSequenceEditor';
import { useApiSequenceGenerationStream } from '../../../../hooks/useApiSequenceGenerationStream';
import { API_URL } from '../../../../config';
import type { SeedDependencyInfo } from './SeedDependencyCard';
import type { WorkflowRevisionApiSequence } from '../hooks/useWorkflowSnapshots';

export type ApiSequenceActions = {
  onEdit: () => void;
  onRegenerate: () => void;
  onClear: () => void;
  isStale: boolean;
};

type ApiSequencePanelProps = {
  workflowId: string;
  nodeCount: number;
  hasSuccessfulRun: boolean;
  workflowFlowType: string;
  workflowApiOnly?: boolean;
  workflowPropertyId?: string | null;
  seedDependencies?: SeedDependencyInfo[];
  /** Captured config shown while previewing an immutable workflow revision. */
  apiSequenceOverride?: WorkflowRevisionApiSequence | null;
  onActionsReady?: (actions: ApiSequenceActions | null) => void;
};

export const ApiSequencePanel: React.FC<ApiSequencePanelProps> = ({
  workflowId,
  nodeCount,
  hasSuccessfulRun,
  workflowFlowType,
  workflowApiOnly = false,
  workflowPropertyId = null,
  seedDependencies,
  apiSequenceOverride,
  onActionsReady,
}) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const sequenceQ = useApiSequence(workflowId);
  const sequence = sequenceQ.data;
  const displayedSequence =
    apiSequenceOverride !== undefined
      ? apiSequenceOverride
        ? {
            id: apiSequenceOverride.id,
            workflowId,
            config: apiSequenceOverride.config,
            status: 'ready' as const,
          }
        : null
      : sequence;

  const isSeed = workflowFlowType === 'seed';
  const seedOutputsQ = useSeedWorkflowOutputs(isSeed ? workflowId : undefined);
  const seedOutputs = isSeed ? seedOutputsQ.data?.outputs : undefined;

  // Check for active generation on mount (survives navigation/refresh)
  const activeGenQ = useQuery({
    queryKey: ['api-sequence-active-generation', workflowId],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api-sequence-agent/active?workflowId=${workflowId}`, {
        credentials: 'include',
      });
      if (!r.ok) return { generationId: null };
      const data = (await r.json()) as { ok: boolean; generationId: string | null };
      return { generationId: data.ok ? data.generationId : null };
    },
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  const generateHook = useApiSequenceGenerationStream(activeGenQ.data?.generationId);
  const saveMutation = useApiSequenceSave();
  const deleteMutation = useApiSequenceDelete(workflowId);
  const apiOnlyToggle = useApiOnlyToggle(workflowId);

  // Optimistic local state for API-only toggle
  const [localApiOnly, setLocalApiOnly] = React.useState(workflowApiOnly);
  React.useEffect(() => {
    setLocalApiOnly(workflowApiOnly);
  }, [workflowApiOnly]);

  // Track the guidance prompt used during generation
  const [lastGuidancePrompt, setLastGuidancePrompt] = React.useState<string | null>(null);

  const updateConfigMutation = useApiSequenceUpdateConfig(workflowId);

  // Editor overlay state
  const [isEditorOpen, setIsEditorOpen] = React.useState(false);
  // Draft config for direct-edit flow when no sequence exists yet
  const [draftConfig, setDraftConfig] = React.useState<ApiSequenceConfig | null>(null);

  // Fetch API requests and credentials for the editor
  const propertyId = workflowPropertyId ?? undefined;
  const { data: apiRequestsData } = useApiRequestList({
    propertyId: propertyId || undefined,
    pageSize: 100,
    enabled: !!propertyId,
  });
  const apiRequests = apiRequestsData?.data || [];

  // When generation completes, save the config
  React.useEffect(() => {
    if (generateHook.status === 'complete' && generateHook.finalConfig && saveMutation.isIdle) {
      const completeEvent = generateHook.events.find((e) => e.type === 'complete');
      const genPropertyId = completeEvent?.propertyId as string | undefined;
      const credentialId = completeEvent?.credentialId as string | null | undefined;

      if (!genPropertyId) {
        toast.error('Could not determine property for saving');
        generateHook.reset();
        return;
      }

      let configToSave = generateHook.finalConfig;
      if (isSeed && seedOutputs && seedOutputs.length > 0) {
        const existingExportKeys = new Set(
          (configToSave.exports ?? []).map((e) => e.variableName)
        );
        const stubExports = seedOutputs
          .filter((o) => !existingExportKeys.has(o.dataKey))
          .map((o) => ({
            variableName: o.dataKey,
            stepId: '',
            responsePath: '',
            description: o.description,
          }));
        if (stubExports.length > 0) {
          configToSave = {
            ...configToSave,
            exports: [...(configToSave.exports ?? []), ...stubExports],
          };
        }
      }

      saveMutation.mutate(
        {
          workflowId,
          credentialId: credentialId ?? null,
          config: configToSave,
          guidancePrompt: lastGuidancePrompt,
        },
        {
          onSuccess: () => {
            toast.success('API sequence generated successfully');
            queryClient
              .invalidateQueries({ queryKey: ['api-sequence', workflowId] })
              .then(() => {
                generateHook.reset();
              });
          },
          onError: (err) => {
            toast.error(`Failed to save: ${err.message}`);
            generateHook.reset();
          },
        }
      );
    }
  }, [generateHook.status, generateHook.finalConfig]);

  const handleGenerate = (guidancePrompt?: string) => {
    setLastGuidancePrompt(guidancePrompt ?? null);
    generateHook.startGeneration({
      endpoint: '/api-sequences/generate',
      body: {
        workflowId,
        ...(guidancePrompt ? { guidancePrompt } : {}),
      },
    });
  };

  const handleGenerateFromPrompt = (prompt: string) => {
    setLastGuidancePrompt(prompt);
    generateHook.startGeneration({
      endpoint: '/api-sequence-agent/generate',
      body: {
        prompt,
        workflowId,
      },
    });
  };

  const handleApiOnlyToggle = (apiOnly: boolean) => {
    setLocalApiOnly(apiOnly);
    apiOnlyToggle.mutate(apiOnly, {
      onSuccess: () => toast.success(apiOnly ? 'API-only mode enabled' : 'API-only mode disabled'),
      onError: (err) => {
        setLocalApiOnly(!apiOnly);
        toast.error(`Failed: ${err.message}`);
      },
    });
  };

  const handleClear = () => {
    if (!sequence) return;
    deleteMutation.mutate(sequence.id, {
      onSuccess: () => {
        toast.success('API sequence cleared');
      },
    });
  };

  const handleEditorSave = (config: ApiSequenceConfig) => {
    if (sequence) {
      updateConfigMutation.mutate(
        { apiSequenceId: sequence.id, config },
        {
          onSuccess: () => {
            toast.success('API sequence updated');
            setIsEditorOpen(false);
          },
          onError: (err) => {
            toast.error(`Failed to save: ${err.message}`);
          },
        }
      );
      return;
    }
    if (!workflowPropertyId) {
      toast.error('Set a property on this workflow before saving');
      return;
    }
    saveMutation.mutate(
      {
        workflowId,
        credentialId: null,
        config,
        guidancePrompt: null,
      },
      {
        onSuccess: () => {
          toast.success('API sequence created');
          setIsEditorOpen(false);
          setDraftConfig(null);
        },
        onError: (err) => {
          toast.error(`Failed to save: ${err.message}`);
        },
      }
    );
  };

  const handleEditDirectly = () => {
    if (!workflowPropertyId) {
      toast.error('Set a property on this workflow before editing directly');
      return;
    }
    setDraftConfig({ steps: [] });
    setIsEditorOpen(true);
  };

  // Report actions to parent for rendering in the header tab row
  React.useEffect(() => {
    if (!onActionsReady) return;
    if (sequence && apiSequenceOverride === undefined && generateHook.status === 'idle') {
      onActionsReady({
        onEdit: () => setIsEditorOpen(true),
        onRegenerate: () => handleGenerate(),
        onClear: handleClear,
        isStale: sequence.status === 'stale',
      });
    } else {
      onActionsReady(null);
    }
    return () => onActionsReady(null);
  }, [sequence, apiSequenceOverride, generateHook.status, onActionsReady]);

  const generateError =
    generateHook.status === 'error'
      ? generateHook.events.find((e) => e.type === 'error')?.message ?? 'Generation failed'
      : null;

  if (sequenceQ.isLoading || activeGenQ.isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (
    !displayedSequence ||
    (apiSequenceOverride === undefined && generateHook.status !== 'idle')
  ) {
    return (
      <>
      <ApiSequenceEmptyState
        workflowId={workflowId}
        nodeCount={nodeCount}
        hasSuccessfulRun={hasSuccessfulRun}
        isGenerating={generateHook.status === 'running'}
        generateStatus={generateHook.status}
        generateEvents={generateHook.events}
        onGenerate={handleGenerate}
        onGenerateFromPrompt={handleGenerateFromPrompt}
        onCancel={generateHook.cancel}
        generateError={generateError}
        apiOnly={localApiOnly}
        onApiOnlyToggle={handleApiOnlyToggle}
        onEditDirectly={localApiOnly ? handleEditDirectly : undefined}
      />
      <AnimatePresence>
        {isEditorOpen && draftConfig && (
          <ApiSequenceEditor
            isOpen={isEditorOpen}
            config={draftConfig}
            apiRequests={apiRequests}
            propertyId={propertyId}
            onSave={handleEditorSave}
            onCancel={() => {
              setIsEditorOpen(false);
              setDraftConfig(null);
            }}
          />
        )}
      </AnimatePresence>
    </>
    );
  }

  return (
    <>
      <ApiSequenceView
        sequence={displayedSequence}
        seedOutputs={seedOutputs}
        seedDependencies={seedDependencies}
      />
      <AnimatePresence>
        {isEditorOpen && (
          <ApiSequenceEditor
            isOpen={isEditorOpen}
            config={displayedSequence.config}
            apiRequests={apiRequests}
            propertyId={propertyId}
            onSave={handleEditorSave}
            onCancel={() => setIsEditorOpen(false)}
          />
        )}
      </AnimatePresence>
    </>
  );
};
